"use client";

import { useEffect, useState } from "react";
import { Settings } from "@/components/icons";
import { hasConsent, setConsent } from "@/lib/cookie-consent";

// IANA timezones for EU member states + the UK. Used to show the consent
// banner only to visitors who are (approximately) in the EU or UK.
const EU_UK_TIMEZONES = new Set([
  // United Kingdom
  "Europe/London",
  // Austria
  "Europe/Vienna",
  // Belgium
  "Europe/Brussels",
  // Bulgaria
  "Europe/Sofia",
  // Croatia
  "Europe/Zagreb",
  // Cyprus
  "Europe/Nicosia",
  "Asia/Nicosia",
  "Asia/Famagusta",
  // Czechia
  "Europe/Prague",
  // Denmark
  "Europe/Copenhagen",
  // Estonia
  "Europe/Tallinn",
  // Finland
  "Europe/Helsinki",
  // France
  "Europe/Paris",
  // Germany
  "Europe/Berlin",
  "Europe/Busingen",
  // Greece
  "Europe/Athens",
  // Hungary
  "Europe/Budapest",
  // Ireland
  "Europe/Dublin",
  // Italy
  "Europe/Rome",
  // Latvia
  "Europe/Riga",
  // Lithuania
  "Europe/Vilnius",
  // Luxembourg
  "Europe/Luxembourg",
  // Malta
  "Europe/Malta",
  // Netherlands
  "Europe/Amsterdam",
  // Poland
  "Europe/Warsaw",
  // Portugal
  "Europe/Lisbon",
  "Atlantic/Madeira",
  "Atlantic/Azores",
  // Romania
  "Europe/Bucharest",
  // Slovakia
  "Europe/Bratislava",
  // Slovenia
  "Europe/Ljubljana",
  // Spain
  "Europe/Madrid",
  "Africa/Ceuta",
  "Atlantic/Canary",
  // Sweden
  "Europe/Stockholm",
]);

function isEuOrUk(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    return EU_UK_TIMEZONES.has(tz);
  } catch {
    return false;
  }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hasConsent() && isEuOrUk()) {
      setVisible(true);
    }
  }, []);

  const choose = (choice: "accepted" | "rejected") => {
    setConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 sm:right-auto sm:max-w-md">
      <div className="rounded-2xl border border-border-secondary bg-white dark:bg-surface-raised p-6 shadow-xl">
        <p className="text-sm leading-relaxed text-text-secondary">
          <span className="text-text-primary">
            Because you&rsquo;re visiting from the EU or UK, we ask before using optional cookies.
          </span>{" "}
          Read our{" "}
          <a
            href="/legal/cookie-policy"
            className="font-medium text-text-primary underline underline-offset-2 hover:text-brand transition-colors"
          >
            cookie policy
          </a>
          .
        </p>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={() => choose("accepted")}
            className="inline-flex h-10 w-full items-center justify-center rounded-xl bg-brand px-5 text-sm font-semibold text-brand-foreground transition-colors hover:bg-brand-hover sm:w-auto"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => choose("rejected")}
            className="inline-flex h-10 w-full items-center justify-center rounded-xl border border-border-secondary bg-transparent px-5 text-sm font-semibold text-text-secondary transition-colors hover:bg-black/5 dark:hover:bg-white/5 sm:w-auto"
          >
            Accept mandatory cookies
          </button>
          <a
            href="/legal/cookie-settings"
            aria-label="Manage cookie settings"
            title="Manage cookie settings"
            className="inline-flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-xl border border-border-secondary bg-transparent text-sm font-semibold text-text-secondary transition-colors hover:bg-black/5 dark:hover:bg-white/5 sm:w-10 sm:gap-0"
          >
            <Settings className="size-4" />
            <span className="sm:hidden">Cookie settings</span>
          </a>
        </div>
      </div>
    </div>
  );
}
